import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@bliks-io/ui';
import {
  CreatePartnerContactRequestSchema,
  type CreatePartnerContactRequest,
  type PartnerContact
} from '@bliks-io/contracts';
import { FormInput, FormTextarea } from '@/components/form/rhf';

interface PartnerContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The contact being edited; absent in create mode. */
  contact?: PartnerContact | null;
  isSubmitting?: boolean;
  onSubmit: (values: CreatePartnerContactRequest) => void;
}

export function PartnerContactDialog({
  open,
  onOpenChange,
  ...formProps
}: PartnerContactDialogProps) {
  const { t } = useTranslation();
  const isCreate = !formProps.contact;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(
              isCreate ? 'partner.contacts.dialog.createTitle' : 'partner.contacts.dialog.editTitle'
            )}
          </DialogTitle>
          <DialogDescription>{t('partner.contacts.dialog.description')}</DialogDescription>
        </DialogHeader>
        {/* Radix unmounts DialogContent's children while closed, so the form is
            a fresh instance on every open. */}
        <PartnerContactForm onOpenChange={onOpenChange} {...formProps} />
      </DialogContent>
    </Dialog>
  );
}

function PartnerContactForm({
  onOpenChange,
  contact,
  isSubmitting,
  onSubmit
}: Omit<PartnerContactDialogProps, 'open'>) {
  const { t } = useTranslation();

  const form = useForm<CreatePartnerContactRequest>({
    resolver: zodResolver(CreatePartnerContactRequestSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: contact?.firstName ?? '',
      lastName: contact?.lastName ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      company: contact?.company ?? '',
      note: contact?.note ?? ''
    }
  });

  return (
    <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <FormInput control={form.control} name="firstName" label={t('common.FirstName')} required />
        <FormInput control={form.control} name="lastName" label={t('common.LastName')} required />
      </div>
      <FormInput
        control={form.control}
        name="company"
        label={t('partner.contacts.columns.company')}
        required
      />
      <div className="grid grid-cols-2 gap-3">
        <FormInput control={form.control} name="email" type="email" label={t('common.Email')} />
        <FormInput
          control={form.control}
          name="phone"
          type="tel"
          label={t('partner.contacts.columns.phone')}
        />
      </div>
      <FormTextarea control={form.control} name="note" label={t('partner.contacts.columns.note')} />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting} loading={isSubmitting}>
          {t('common.Save')}
        </Button>
      </DialogFooter>
    </form>
  );
}
